import { filter } from 'lodash';
import * as React from 'react';
// @ts-ignore
import decamelizeKeys from 'decamelize-keys';
import Icon, { IconSize } from 'components/Icon';
import TooltipTrigger from 'components/TooltipTrigger';
import ScrollLoadZone from 'components/ScrollLoadZone';
import {
  ComposableTable,
  getArrayTextCellGenerator,
  getFamilyCellGenerator,
  getIconCellGenerator,
  getTextCellGenerator,
  getTextWithSubtextCellGenerator,
  getDatestampCellGenerator,
  getUserAvatarCheckboxCellGenerator,
  ColumnGeneratorFunc,
} from 'components/ComposableTable';
import Text from 'components/Text';
import Link from 'components/Link';
import RosterConstants from 'constants/RosterConstants';
import Group from 'entities/Group';
import ModalTrigger from 'components/ModalTrigger';
// @ts-ignore
import UsersInviteModal from 'users/components/UsersInviteModal';
import Row from 'components/Row';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import sisSyncPath from 'admin/lib/sisSyncPath';

// @ts-ignore
import { UNKNOWN, formatRole } from 'utils/roleHelpers';

// @ts-ignore
import { UserRole, GroupInfo } from './PeopleList';
import { ColumnHeader, SortDirection } from 'components/ComposableTable';
import { GetOrganizationMembers_organizations_members_members } from './queries/__generated__/GetOrganizationMembers';
import { GetGroupMembers_classes_members_members } from './queries/__generated__/GetGroupMembers';

import './styles/PeopleListTable.scss';

// FIX ME: this should actually be a union type, not an intersection
// but it causes all sorts of TS errors that need fixing.
export type PeopleListRow = GetOrganizationMembers_organizations_members_members &
  GetGroupMembers_classes_members_members;

type Props = {
  entityUuid: string;
  entityType: string;
  hasNextPage: boolean;
  loading: boolean;
  fetchMore: () => Promise<any>;
  members: PeopleListRow[];
  role: UserRole;
  onPersonClicked: (person: any) => void;
  groupInfo: GroupInfo;
  onUserAddedToSelection: (user: PeopleListRow) => void;
  onUserRemovedFromSelection: (user: PeopleListRow) => void;
  onUserChanged: () => void;
  onSort: (sortColumn: string, sortDirection?: SortDirection) => void;
  batchSelectedUserIds: number[];
  openUserId?: number | null;
  hasRosterSource: boolean;
};

export default class PeopleListTable extends React.Component<Props> {
  toggleUser = (row: PeopleListRow) => {
    const { batchSelectedUserIds, onUserAddedToSelection, onUserRemovedFromSelection } = this.props;
    if (batchSelectedUserIds.includes(row.user.id)) {
      onUserRemovedFromSelection(row);
    } else {
      onUserAddedToSelection(row);
    }
  };

  handleAvatarClicked = (event: React.SyntheticEvent, row: PeopleListRow) => {
    event.stopPropagation();
    const { onUserAddedToSelection } = this.props;
    onUserAddedToSelection(row);
  };

  handleCheckboxClicked = (event: React.SyntheticEvent, row: PeopleListRow) => {
    event.stopPropagation();
    this.toggleUser(row);
  };

  columnHeaderForColumn = (columnName: string): ColumnHeader => {
    const columnMap: { [key: string]: ColumnHeader } = {
      avatarCheckbox: {
        name: '',
        type: 'avatar',
      },
      classes: {
        name: 'Classes',
        icon: {
          iconName: 'question-circle',
          tooltip: 'The number of classes owned and joined',
        },
        width: 45,
      },
      familyMembers: {
        name: 'Family members',
        width: 175,
      },
      grade: {
        name: 'Grade',
        sortValue: 'grade',
        width: 36,
      },
      lastAnnouncementSent: {
        name: 'Last announcement sent',
        sortValue: 'last_broadcast_sent_on',
        width: 144,
      },
      name: {
        name: 'Name',
        sortValue: 'first_name',
        width: 145,
      },
      role: {
        name: 'Role',
        sortValue: 'role',
        width: 100,
      },
      source: {
        name: 'SIS link',
        icon: {
          iconName: 'question-circle',
          tooltip:
            'Linked users are rostered from your school’s database. Users who aren’t linked signed up individually or were invited by others.',
        },
        width: 65,
      },
      dateJoined: {
        name: 'Date joined',
        sortValue: 'join_date',
      },
    };

    return columnMap[columnName];
  };

  getHeaders = (): string[] => {
    const { role, hasRosterSource, entityType } = this.props;

    // TODO: move header generation to data container, and have separate data containers for group/org
    // TODO: get other needed columns working in the query
    if (entityType === 'group') {
      return ['avatarCheckbox', 'name', 'dateJoined', 'role', 'familyMembers'];
    }

    const columns: { [key: string]: string[] } = {
      everyone: ['avatarCheckbox', 'name', 'source', 'role', 'classes'],
      teacher: ['avatarCheckbox', 'name', 'source', 'lastAnnouncementSent', 'classes'],
      student: ['avatarCheckbox', 'name', 'source', 'grade', 'classes', 'familyMembers'],
      parent: ['avatarCheckbox', 'name', 'source', 'classes', 'familyMembers'],
      unknown: ['avatarCheckbox', 'name', 'source', 'classes'],
    };

    if (!hasRosterSource) {
      return columns[role].filter(c => c !== 'source' && c !== 'grade');
    }

    return columns[role];
  };

  columnHeaders = (): Array<ColumnHeader> => {
    return this.getHeaders().map(name => this.columnHeaderForColumn(name));
  };

  isCurrentUser = (id: number) => {
    return id === CurrentUser.getUser().id;
  };

  columnGenerators = (): ColumnGeneratorFunc<PeopleListRow>[] => {
    const { batchSelectedUserIds, onUserChanged, onPersonClicked, entityUuid } = this.props;
    const iconProps: { size: IconSize } = {
      size: 'large',
    };

    const components: { [key: string]: ColumnGeneratorFunc<PeopleListRow> } = {
      avatarCheckbox: getUserAvatarCheckboxCellGenerator({
        checked: (data: PeopleListRow) => batchSelectedUserIds.includes(data.user.id),
        hideAvatar: batchSelectedUserIds.length > 0,
        onAvatarClickAction: this.handleAvatarClicked,
        onCheckboxClickAction: this.handleCheckboxClicked,
        userField: (row: PeopleListRow) => row.user,
      }),
      classes: getTextCellGenerator(
        (row: PeopleListRow) =>
          row.orgUserStats.groupsJoinedCount + row.orgUserStats.groupsOwnedCount
      ),
      familyMembers: getFamilyCellGenerator(
        (row: PeopleListRow) => row.user.familySummary ?? [],
        (row: PeopleListRow) => ({
          userId: row.user.id,
          userUuid: row.user.uuid,
          canAddChild: row.userInteractionPermissions.canAddChild,
          canAddParent: row.userInteractionPermissions.canAddParent,
          maxProfilePics: 4,
          staticProfilePicture: true,
          onAddFamilyMember: () => {
            EventTracking.trackKey('school_people_list.table.family_members.added', {
              organization_uuid: entityUuid,
            });
            onUserChanged();
          },
          onFamilyMemberClick: (user: any, role: string) => onPersonClicked({ user, role }),
        })
      ),
      grade: getTextCellGenerator('grade'),
      lastAnnouncementSent: getDatestampCellGenerator(
        (row: PeopleListRow) =>
          row.orgUserStats.lastBroadcastSentOn ? row.orgUserStats.lastBroadcastSentOn * 1000 : ''
      ),
      name: getTextWithSubtextCellGenerator({
        subtext: (row: PeopleListRow) =>
          this.isCurrentUser(row.user.id) ? <Text italic>You</Text> : row.user.note ?? '',
        text: (row: PeopleListRow) => filter([row.user.firstName, row.user.lastName]).join(' '),
        textWithSubtextProps: {
          className: 'PeopleListTable-name-text',
        },
      }),
      role: getArrayTextCellGenerator(
        (row: PeopleListRow) => {
          const roles = row.roles.length > 0 ? row.roles : [UNKNOWN];
          // returned roles array is read-only, therefore return sorted copy of array
          return [...roles].sort();
        },
        ', ',
        (r: string) => formatRole(r, 1) ?? ''
      ),
      source: getIconCellGenerator('rosteredStatus', {
        true: (
          <TooltipTrigger tooltip={RosterConstants.rosteredLabel} placement="top">
            <span>
              <Icon {...iconProps} name="link-linked" color="shamrock" />
            </span>
          </TooltipTrigger>
        ),
        false: (
          <TooltipTrigger tooltip={RosterConstants.notRosteredLabel} placement="top">
            <span>
              <Icon {...iconProps} name="link-unlinked" color="koala" />
            </span>
          </TooltipTrigger>
        ),
      }),
      dateJoined: getDatestampCellGenerator((row: PeopleListRow) => row.created_at ?? ''),
    };

    return this.getHeaders().map(name => components[name]);
  };

  renderNoResultsView = () => {
    const { entityType, entityUuid, groupInfo, loading } = this.props;
    const group = Group.fromJSON(decamelizeKeys(groupInfo));

    if (loading) {
      return null;
    }

    if (entityType === 'organization') {
      return (
        <div data-test="no-results-view">
          <Text align="center" size="medium">
            There’s no one here!{' '}
            <ModalTrigger modal={<UsersInviteModal group={group} />}>
              <Link>Add people directly</Link>
            </ModalTrigger>{' '}
            or <Link to={sisSyncPath(entityUuid)}>sync your SIS</Link> with Remind.
          </Text>
        </div>
      );
    } else if (entityType === 'group') {
      return (
        <div data-test="no-results-view">
          <Text align="center" size="medium">
            There’s no one here!{' '}
            <ModalTrigger modal={<UsersInviteModal group={group} />}>
              <Link>Invite people to join</Link>
            </ModalTrigger>
          </Text>
        </div>
      );
    }

    return null;
  };

  render() {
    const {
      fetchMore,
      hasNextPage,
      loading,
      members,
      onPersonClicked,
      onSort,
      batchSelectedUserIds,
      openUserId,
    } = this.props;

    return (
      <div className="PeopleListTable">
        <Row size="large">
          <ComposableTable
            columnGenerators={this.columnGenerators()}
            columnHeaders={this.columnHeaders()}
            fixedHeader
            handleRowClick={onPersonClicked}
            handleSort={onSort}
            keyField={data => data.user.id}
            keysOfActiveRows={
              openUserId ? [openUserId, ...batchSelectedUserIds] : batchSelectedUserIds
            }
            loading={loading}
            rows={members}
            className="PeopleListTable"
          />
        </Row>
        {!loading && hasNextPage && <ScrollLoadZone threshold={500} onZoneActivated={fetchMore} />}
        {members && members.length === 0 && this.renderNoResultsView()}
      </div>
    );
  }
}
